/**
 * The API client.
 *
 * One place that knows about headers, error shape, and the donor key. Every
 * failed request throws an `ApiError` carrying the server's message, so screens
 * can show what actually went wrong instead of a generic failure.
 */

const DONOR_STORAGE_KEY = 'gompa.donorKey';

/**
 * The donor key stands in for an account until there is one.
 *
 * It scopes saved monasteries and offering history to this browser. It is NOT
 * a credential — it is guessable and lives in localStorage — so nothing behind
 * it may ever be sensitive. Real sessions replace it before anything private
 * lands there.
 */
export function getDonorKey() {
  if (typeof localStorage === 'undefined') return 'anonymous';
  let key = localStorage.getItem(DONOR_STORAGE_KEY);
  if (!key) {
    key = `guest-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(DONOR_STORAGE_KEY, key);
  }
  return key;
}

export class ApiError extends Error {
  constructor(message, { status, details } = {}) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/**
 * The CSRF cookie is deliberately readable — that is the double-submit
 * half (see `server/src/middleware/session.js`'s `csrfGuard`). It is only
 * ever set once a donor has an actual session (signed in via `/account`),
 * so this is `null` for the common case of an anonymous browser and the
 * header below is simply omitted — `csrfGuard` itself skips the check
 * entirely when there is no session cookie to protect.
 *
 * Before this existed, ANY signed-in donor's first state-changing request
 * here — saving a monastery, placing an order, anything — 403'd outright:
 * this client sent the donor key but never the CSRF header portal/api.ts
 * already sends, so `csrfGuard` saw a session cookie with no matching
 * header and refused every one of them. Found by testing the save-heart
 * flow signed in as a portal account in the same browser; a signed-in
 * donor is the same shape of session, so the bug was real for them too,
 * not just for staff testing.
 */
function csrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)gompa_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function request(path, { method = 'GET', body, signal } = {}) {
  let response;
  try {
    const token = csrfToken();
    response = await fetch(`/api${path}`, {
      method,
      signal,
      headers: {
        'X-Gompa-Donor': getDonorKey(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token && method !== 'GET' ? { 'x-gompa-csrf': token } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    if (cause.name === 'AbortError') throw cause;
    // A network failure is the one error the server cannot describe, so it is
    // the one the client has to word itself.
    throw new ApiError('Could not reach the Gompa server. Check your connection and try again.');
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(payload.error || 'Something went wrong.', {
      status: response.status,
      details: payload.details,
    });
  }
  return payload;
}

export const api = {
  // Firestore/v1 — the catalogue the admin and partner portals actually
  // manage. There used to be a second, unversioned catalogue here
  // (`/monasteries`, backed by a static in-memory file with non-UUID ids);
  // it's why the claim form used to fail with "Invalid UUID" — a monastery
  // browsed from that catalogue had no corresponding Firestore document at
  // all. That catalogue is not touched by this client anymore.
  monasteries: (params = {}, signal) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return request(`/v1/monasteries${query ? `?${query}` : ''}`, { signal });
  },
  // Returns the full monastery, services and all — a separate per-service
  // fetch isn't needed, unlike the old catalogue's `/services/:id` route.
  monastery: (id, signal) => request(`/v1/monasteries/${id}`, { signal }),
  // The full-screen "About" reader: the monastery's long-form text plus a
  // cleaned Wikipedia article when one is linked (see server lib/wikipedia.js).
  monasteryAbout: (id, signal) => request(`/v1/monasteries/${id}/about`, { signal }),
  // A monastery's public events (festivals, teachings, open pujas). Published
  // only; drafts stay in the partner console. See server routes/v1/catalogue.js.
  monasteryEvents: (id, signal) => request(`/v1/monasteries/${id}/events`, { signal }),
  categories: (signal) => request('/v1/monasteries/categories', { signal }),
  lineages: (signal) => request('/v1/monasteries/lineages', { signal }),

  quote: (body) => request('/v1/orders/quote', { method: 'POST', body }),
  createOrder: (body) => request('/v1/orders', { method: 'POST', body }),
  orders: (signal) => request('/v1/orders', { signal }),
  order: (reference, signal) => request(`/v1/orders/${reference}`, { signal }),
  // The client secret + publishable key for this order's Stripe payment. One
  // PaymentIntent per order — a reload or a retry returns the same one.
  orderPaymentIntent: (reference) =>
    request(`/v1/orders/${reference}/payment-intent`, { method: 'POST' }),
  wall: (limit, signal) => request(`/v1/orders/wall?limit=${limit}`, { signal }),

  saved: (signal) => request('/v1/account/saved', { signal }),
  setSaved: (ids) => request('/v1/account/saved', { method: 'PUT', body: { ids } }),

  completeRegistration: (body) => request('/v1/registration', { method: 'PUT', body }),

  landing: (signal) => request('/content/landing', { signal }),
  about: (signal) => request('/content/about', { signal }),
  legal: (slug, signal) => request(`/content/legal/${slug}`, { signal }),
  categoryMeanings: (signal) => request('/content/category-meanings', { signal }),
  newsletter: (email) => request('/content/newsletter', { method: 'POST', body: { email } }),
  message: (body) => request('/content/messages', { method: 'POST', body }),

  /** Public — no session. Only records a claim request; a staff-mediated
   * onboarding invite is issued later, if the claim is approved. */
  submitClaim: (body) => request('/v1/claims', { method: 'POST', body }),
};
